import type { FlowQuery, TaskQuery } from '../types/common'
import { getFlows, getTasksForFlows } from './flow.service'

export type LegacyFlowConfig = {
  apiGatewayUrl?: string
  tokenUrl?: string
  clientId?: string
  clientSecret?: string
  tokenScope?: string
}

export type LegacyFlow<T> = {
  flowId: string
  createdAt: string
  hasErrors?: boolean
  data: T
}

type LegacyResult<T> = { status: number; data?: T; message?: string }

const FLOW_PROCESS_ADMIN = JSON.stringify({ role: ['admin'] })
const TOKEN_EXPIRY_MARGIN_MS = 30_000

const legacyTokens = new Map<string, { token: string; expiresAt: number }>()

export function legacyBudgetBatchQuery(email: string) {
  return `flowDefinitionId=budget-batch&status=active&data.flowEndStatus=!&data.budgetData.email=${encodeURIComponent(email)}&data.isIncludedInSigningBatch=false&view=budget-batch`
}

export function legacySigningBatchQuery(email: string) {
  return `flowDefinitionId=signing-batch&status=active&data.signing.signingTask.status=CREATED&data.signer.email=${email}&view=signing-batch`
}

export function legacySigningBatchFlowQuery(flowId: string, email: string) {
  return `flowDefinitionId=signing-batch&status=active&flowId=${flowId}&data.signer.email=${encodeURIComponent(email)}&view=signing-batch`
}

export function legacySelectionQuery(email: string) {
  return `flowDefinitionId=budget-selection&status=active&data.flowEndStatus=!&data.budgetData.contactEmail=${email}&view=budget-selection`
}

export function legacySelectionTaskQuery(flowId: string) {
  return `flowId=${flowId}&status=pending&taskCategory=user-task&taskType=select-budget-selections`
}

export async function getLegacyToken(config: LegacyFlowConfig): Promise<LegacyResult<string>> {
  const cacheKey = [config.tokenUrl, config.clientId, config.tokenScope].join('|')
  const cached = legacyTokens.get(cacheKey)
  if (cached && cached.expiresAt > Date.now()) return { status: 200, data: cached.token }

  const response = await fetch(config.tokenUrl ?? '', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'client_credentials',
      client_id: config.clientId ?? '',
      client_secret: config.clientSecret ?? '',
      scope: config.tokenScope ?? '',
    }),
  })

  const { ok, status } = response
  if (!ok) return { status, message: `Error fetching legacy token: ${status} - ${await response.text()}` }

  const { access_token: token, expires_in: expiresIn } = (await response.json()) ?? {}
  if (!token) return { status: 400, message: 'Legacy token response contained no access_token' }

  if (typeof expiresIn === 'number') {
    legacyTokens.set(cacheKey, { token, expiresAt: Date.now() + expiresIn * 1000 - TOKEN_EXPIRY_MARGIN_MS })
  }

  return { status: 200, data: token }
}

export function getLegacyHeaders(token: string) {
  return {
    'flow-process-user': FLOW_PROCESS_ADMIN,
    Authorization: `Bearer ${token}`,
  }
}

export function legacyBaseUrl(config: LegacyFlowConfig) {
  return `${config.apiGatewayUrl}/legacy-flow`
}

export async function legacyRequest<T>(
  path: string,
  config: LegacyFlowConfig,
  init?: { method?: string; body?: unknown },
): Promise<LegacyResult<T>> {
  const { status: tokenStatus, message, data: token } = await getLegacyToken(config)
  if (!token) return { status: tokenStatus, message }

  const response = await fetch(`${legacyBaseUrl(config)}${path}`, {
    method: init?.method ?? 'GET',
    headers: {
      ...getLegacyHeaders(token),
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(init?.body ? { body: JSON.stringify(init.body) } : {}),
  })

  const { ok, status } = response
  if (!ok) return { status, message: `Legacy request ${path} failed: ${status} - ${await response.text()}` }

  return { status: 200, data: (await response.json().catch(() => undefined)) as T }
}

export async function getLegacyFlows<T>(query: string, config: LegacyFlowConfig): Promise<LegacyFlow<T>[]> {
  const { message, data } = await legacyRequest<{ flows?: LegacyFlow<T>[] }>(`/flows?${query}`, config)
  if (message) {
    console.error(message)
    return []
  }

  return data?.flows ?? []
}

export async function getFlowsWithPendingTask(query: FlowQuery, taskQuery: TaskQuery, userHeader = FLOW_PROCESS_ADMIN) {
  const { flows = [] } = (await getFlows(query, userHeader)) ?? {}
  if (flows.length === 0) return []

  const tasks = await getTasksForFlows(
    flows.map((flow) => flow.flowId),
    { ...taskQuery, stripTaskContext: true, stripTaskVariables: true, stripTaskDefaults: true },
    userHeader,
  )
  const flowIdsWithTask = new Set(tasks.map((task) => task.flowId))

  return flows.filter((flow) => flowIdsWithTask.has(flow.flowId))
}
